import { useState, useEffect, useRef } from 'react';
import { Flame, Monitor, Key, Camera, Radio, X, Phone, Mail, ChevronRight, Star, Award, TrendingUp, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { reportPhoneConversion, trackButtonClick, trackEvent } from '@/utils/analytics';

type Mode = 'fixed' | 'sticky' | 'inline';

interface QuickAction {
  id: string;
  label: string;
  icon: typeof Flame;
  quotePath: string;
  color: string;
  gradient: string;
  description: string;
}

const quickActions: QuickAction[] = [
  {
    id: 'fire-alarm',
    label: 'Fire Alarm',
    icon: Flame,
    quotePath: '/quotes/services/fire-alarm',
    color: 'bg-red-500',
    gradient: 'from-red-500 via-red-600 to-orange-600',
    description: 'Get Quote',
  },
  {
    id: 'monitoring',
    label: '24/7 Monitoring',
    icon: Monitor,
    quotePath: '/quotes/services/24-7-monitoring',
    color: 'bg-green-500',
    gradient: 'from-green-500 via-emerald-500 to-teal-600',
    description: 'Get Quote',
  },
  {
    id: 'access-control',
    label: 'Access Control',
    icon: Key,
    quotePath: '/quotes/services/access-control',
    color: 'bg-amber-500',
    gradient: 'from-amber-500 via-yellow-500 to-orange-500',
    description: 'Get Quote',
  },
  {
    id: 'cctv',
    label: 'CCTV Systems',
    icon: Camera,
    quotePath: '/quotes/services/cctv-systems',
    color: 'bg-blue-500',
    gradient: 'from-blue-500 via-cyan-500 to-sky-600',
    description: 'Get Quote',
  },
  {
    id: 'errcs',
    label: 'ERRCS',
    icon: Radio,
    quotePath: '/contact',
    color: 'bg-purple-500',
    gradient: 'from-purple-500 via-indigo-500 to-violet-600',
    description: 'Two-Way Communication',
  },
];

interface QuickActionsButtonProps {
  mode?: Mode;
}

export function QuickActionsButton({ mode = 'fixed' }: QuickActionsButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside or pressing Escape
  // Ensure body scrolling is not blocked when menu is open
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking inside menu, button, or container
      if (
        menuRef.current?.contains(target) ||
        buttonRef.current?.contains(target) ||
        containerRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Ensure body can still scroll when menu is open
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.documentElement.style.overflow = '';
      
      // Use click instead of mousedown to allow button clicks to register first
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
        document.removeEventListener('keydown', handleEscape);
        // Clean up any overflow styles
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.documentElement.style.overflow = '';
      };
    }
  }, [isOpen]);

  const handleActionClick = (action: QuickAction) => {
    trackButtonClick(`Quick Quote - ${action.label}`, 'floating_button');
    trackEvent('quick_quote_click', 'conversion', action.id);
    navigate(action.quotePath);
    setIsOpen(false);
  };

  const handlePhoneClick = () => {
    trackButtonClick('Quick Call', 'floating_button');
    trackEvent('quick_call_click', 'conversion', 'phone');
    reportPhoneConversion();
  };

  const handleContactClick = () => {
    trackButtonClick('Quick Contact', 'floating_button');
    navigate('/contact');
    setIsOpen(false);
  };

  const MainButton = (
    <button
      ref={buttonRef}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
        trackEvent('quick_actions_toggle', 'engagement', isOpen ? 'close' : 'open');
      }}
      className="group relative h-12 w-12 rounded-full focus:outline-none focus:ring-4 focus:ring-[#e69138]/50 focus:ring-offset-2 transition-all duration-300 z-[2001] cursor-pointer"
      aria-label="Quick Actions"
      aria-expanded={isOpen}
      aria-haspopup="true"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Outer glow - more intense when open */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute -inset-3 rounded-full bg-[radial-gradient(circle_at_center,rgba(230,145,56,0.35),transparent_60%)] blur-xl transition-opacity duration-300",
          isOpen ? "opacity-100 scale-125" : "opacity-80 group-hover:opacity-100"
        )}
      />
      
      {/* Button face */}
      <span
        className={cn(
          "relative flex h-full w-full items-center justify-center rounded-full bg-[#e69138] bg-gradient-to-tr from-[#cf0202] via-[#e69138] to-[#f1c232] shadow-[0_8px_20px_rgba(230,145,56,0.35)] ring-2 ring-white/10 transition-all duration-300",
          "hover:shadow-[0_12px_30px_rgba(230,145,56,0.5)]",
          isOpen ? "scale-110 rotate-45 shadow-[0_12px_30px_rgba(230,145,56,0.5)]" : "group-hover:scale-110"
        )}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white drop-shadow transition-transform duration-300" aria-hidden="true" />
        ) : (
          <div className="flex flex-col gap-1" aria-hidden="true">
            <div className="w-6 h-0.5 bg-white rounded transition-all duration-300" />
            <div className="w-6 h-0.5 bg-white rounded transition-all duration-300" />
            <div className="w-6 h-0.5 bg-white rounded transition-all duration-300" />
          </div>
        )}
      </span>
    </button>
  );

  const Menu = (
    <div
      ref={menuRef}
      className={cn(
        "w-[85vw] sm:w-80 rounded-lg bg-gradient-to-br from-white to-gray-50 shadow-xl border border-[#e69138]/30 transition-all duration-300",
        "backdrop-blur-sm max-w-[320px]",
        "flex flex-col overflow-hidden",
        "opacity-100 translate-y-0 scale-100"
      )}
      style={{
        maxHeight: isOpen ? 'calc(80vh - 8rem)' : '0',
        pointerEvents: 'auto',
        display: 'flex',
      }}
    >
      {/* Header */}
      <div className="relative bg-gradient-to-r from-[#990000] via-[#e69138] to-[#f1c232] px-3 py-2 overflow-hidden border-b-2 border-[#e69138]/50">
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white drop-shadow-lg">Quick Service Paths</h3>
            </div>
            {/* Trust badges */}
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-white/20 border border-white/30 rounded text-[9px]">
                <Award className="h-2.5 w-2.5 text-white" />
                <span className="text-white font-semibold">38+ Yrs</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions List - Scrollable */}
      <div 
        className="p-2 space-y-2 overflow-y-auto overflow-x-hidden bg-white" 
        style={{ 
          scrollbarWidth: 'thin', 
          scrollbarColor: 'rgba(230,145,56,0.5) transparent',
          maxHeight: 'calc(60vh - 8rem)', // Dynamic max height accounting for CTA bar
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          overscrollBehavior: 'contain', // Prevent scroll chaining
          touchAction: 'pan-y' // Allow vertical scrolling on touch devices
        }}
      >
        {quickActions.map((action, index) => {
          const Icon = action.icon;
          
          return (
            <button
              key={action.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isOpen) {
                  handleActionClick(action);
                }
              }}
              className={cn(
                "w-full relative group overflow-hidden rounded-lg border border-[#e69138]/30 bg-gradient-to-r from-white to-gray-50",
                "hover:border-[#e69138] hover:shadow-md hover:shadow-[#e69138]/20 transition-all duration-300",
                "hover:scale-[1.01] active:scale-[0.99]",
                "focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-1 focus:ring-offset-white",
                "cursor-pointer"
              )}
              style={{
                animationDelay: `${index * 50}ms`,
                opacity: 1,
                transform: 'translateX(0)',
                transition: `all 0.3s ease ${index * 50}ms`,
                pointerEvents: isOpen ? 'auto' : 'none',
                zIndex: 2002,
                position: 'relative',
              }}
              disabled={!isOpen}
              aria-label={`Get quote for ${action.label}`}
            >
              {/* Hover background glow */}
              <div className={cn(
                "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500",
                "bg-gradient-to-r from-[#e69138]/10 via-transparent to-[#f1c232]/10"
              )} />
              
              {/* Hover border effect */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute inset-0 rounded-xl border-2 border-[#e69138] shadow-[0_0_20px_rgba(230,145,56,0.3)]" />
              </div>
              
              <div className="relative p-2.5 flex items-center gap-3">
                {/* Icon */}
                <div 
                  className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center relative",
                    "ring-1 ring-[#e69138]/30 group-hover:ring-[#e69138] transition-all duration-300",
                    "group-hover:scale-105 group-hover:shadow-md group-hover:shadow-[#e69138]/30",
                    action.gradient
                  )}
                >
                  <Icon className="h-5 w-5 text-white drop-shadow-lg relative z-10" aria-hidden="true" />
                </div>
                
                {/* Content */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="font-semibold text-gray-900 text-sm group-hover:text-[#990000] transition-colors">
                      {action.label}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600">{action.description}</div>
                </div>
                
                {/* Arrow */}
                <div className="flex-shrink-0 relative">
                  <ChevronRight className="h-4 w-4 text-[#e69138] group-hover:text-[#990000] group-hover:translate-x-0.5 transition-all duration-200" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Trust Indicators Section */}
      <div className="border-t border-[#e69138]/30 px-2 py-1.5 bg-gradient-to-r from-gray-50 to-white relative overflow-hidden">
        <div className="relative grid grid-cols-3 gap-1 text-center">
          <div className="flex flex-col items-center gap-0.5 group cursor-default">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-2 w-2 fill-[#e69138] text-[#e69138]" />
              ))}
            </div>
            <span className="text-[8px] text-gray-700 font-medium">4.9/5</span>
          </div>
          <div className="flex flex-col items-center gap-0.5 border-x border-gray-300 group cursor-default">
            <Shield className="h-3 w-3 text-[#e69138]" />
            <span className="text-[8px] text-gray-700 font-medium">Licensed</span>
          </div>
          <div className="flex flex-col items-center gap-0.5 group cursor-default">
            <TrendingUp className="h-3 w-3 text-[#e69138]" />
            <span className="text-[8px] text-gray-700 font-medium">38+ Yrs</span>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="border-t border-[#e69138]/30 p-2 space-y-1.5 bg-gradient-to-b from-gray-50 to-white">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isOpen) {
              handlePhoneClick();
            }
          }}
          className="w-full relative flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-gradient-to-r from-[#990000] via-[#e69138] to-[#f1c232] text-white font-semibold hover:from-[#cc0000] hover:via-[#d17f2a] hover:to-[#e6b800] transition-all duration-300 hover:scale-[1.01] shadow-md hover:shadow-lg hover:shadow-[#e69138]/50 active:scale-[0.99] group focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-1 focus:ring-offset-white overflow-hidden cursor-pointer"
          style={{ pointerEvents: isOpen ? 'auto' : 'none', zIndex: 2002, position: 'relative' }}
          disabled={!isOpen}
          aria-label="Call (800) 540-2140"
        >
          <Phone className="h-4 w-4 group-hover:scale-110 transition-transform" aria-hidden="true" />
          <span className="text-xs sm:text-sm">Call (800) 540-2140</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isOpen) {
              handleContactClick();
            }
          }}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-[#e69138]/30 bg-white text-[#990000] font-semibold hover:bg-gray-50 hover:border-[#e69138] hover:text-[#990000] hover:shadow-md hover:shadow-[#e69138]/20 transition-all duration-300 active:scale-[0.99] group focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-1 focus:ring-offset-white relative overflow-hidden cursor-pointer"
          style={{ pointerEvents: isOpen ? 'auto' : 'none', zIndex: 2002, position: 'relative' }}
          disabled={!isOpen}
          aria-label="Contact Us"
        >
          <Mail className="h-4 w-4 group-hover:scale-110 transition-transform" aria-hidden="true" />
          <span className="text-xs sm:text-sm">Contact Support</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-10px) translateX(5px); }
          50% { transform: translateY(-5px) translateX(-5px); }
          75% { transform: translateY(-15px) translateX(3px); }
        }
        @keyframes slide-in-right {
          0% { opacity: 0; transform: translateX(-20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.5s ease-out;
        }
        /* Custom scrollbar styling */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: rgba(229, 231, 235, 0.5);
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(230, 145, 56, 0.5);
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(230, 145, 56, 0.7);
        }
      `}</style>
      {mode === 'fixed' && (
        <div 
          ref={containerRef}
          className="fixed left-auto right-4 sm:right-6 z-[2000]" 
          style={{ 
            right: 'max(1rem, env(safe-area-inset-right))', 
            bottom: 'calc(var(--sticky-cta-offset, 140px) + 1rem)',
            pointerEvents: 'none' 
          }}
        >
          <div style={{ pointerEvents: 'auto', position: 'relative' }}>
            {MainButton}
          </div>
          {isOpen && (
            <div 
              className="absolute right-0" 
              style={{ 
                bottom: 'calc(100% + 1rem)',
                pointerEvents: 'auto', 
                position: 'absolute',
                zIndex: 2001,
                display: 'flex',
                opacity: 1,
                visibility: 'visible',
                maxHeight: 'calc(100vh - var(--sticky-cta-offset, 140px) - 10rem)',
                overflowY: 'auto',
              }}
            >
              {Menu}
            </div>
          )}
        </div>
      )}
      {mode === 'sticky' && (
        <div
          className="sticky top-[calc(100vh-6rem)] z-[30] flex justify-end pr-4 sm:pr-8"
          style={{ paddingRight: 'max(1rem, env(safe-area-inset-right))', pointerEvents: 'none' }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            {MainButton}
            {Menu}
          </div>
        </div>
      )}
      {mode === 'inline' && (
        <div className="relative z-[10] flex justify-end pr-4 sm:pr-8">
          {MainButton}
          {Menu}
        </div>
      )}
    </>
  );
}
